import { moment } from '@eventbrite/date';
import { HandWithMoneyGreen } from '@eventbrite/eds-iconography';
import { Illustration } from '@eventbrite/eds-illustration';
import { Modal } from '@eventbrite/eds-modal';
import { gettext } from '@eventbrite/i18n';
import React, { useEffect, useState } from 'react';
import { BANK_ACCOUNTS, FINANCE_SETTINGS, ORGANIZATIONS } from '../constants';
import { EventInfo } from '../types';
import { getSessionStorageOrDefault, navigateTo } from '../utils';
import './PayoutMethodRequestEventEndedModal.scss';

interface ModalContentProps {
    amount: string;
    endDate: string;
    endTime: string;
    logo: string;
    name: string;
}

export const PAYOUT_METHOD_REQUEST_EVENT_ENDED_KEY =
    'payout_method_request_event_ended_modal_seen';

const ModalContent: React.FC<ModalContentProps> = ({
    amount,
    endDate,
    endTime,
    logo,
    name,
}) => (
    <section className="eds-text-bm payout-method-request-event-ended-modal">
        <p>
            {gettext(
                'To receive your %(amount)s payout from your event that ended on %(endDate)s, add a bank account.',
                {
                    amount,
                    endDate,
                },
            )}
        </p>
        <p>
            {gettext(
                "We can't deliver your funds until you add a bank account.",
            )}
        </p>
        <div className="event-card">
            <div className="event-card__logo-wrapper">
                <img className="event-card__logo" src={logo} alt={name} />
            </div>
            <div className="event-card__info">
                <div className="event-card__status-info eds-text-bs eds-text-color--primary-brand">
                    <div className="event-card__end-date">
                        {endDate} • {endTime}
                    </div>
                    <div className="event-card__status">{gettext('ENDED')}</div>
                </div>
                <div className="event-card__name eds-text-weight--semibold">
                    {name}
                </div>
            </div>
        </div>
    </section>
);

export const PayoutMethodRequestEventEndedModal: React.FC<{
    country: string;
    event: EventInfo;
    organizationId: string;
}> = ({ country, event, organizationId }) => {
    const {
        currency,
        endDate,
        id: eventId,
        locale,
        logo,
        name,
        payoutAmount,
    } = event;
    const formattedLocale = locale?.replace('_', '-');
    const localizedPayoutAmount = payoutAmount?.toLocaleString(
        formattedLocale,
        {
            style: 'currency',
            currency,
        },
    );
    const formattedEndDate = moment(endDate).format('dddd, MMMM DD');
    const formattedEndTime = moment(endDate).format('h:mma');
    const keyModalSeen = `${organizationId}_${PAYOUT_METHOD_REQUEST_EVENT_ENDED_KEY}`;

    const [isShown, setIsShown] = useState(false);
    const [modalSeen, setModalSeen] = useState(
        getSessionStorageOrDefault(keyModalSeen, false),
    );

    useEffect(() => {
        if (!modalSeen) {
            setIsShown(true);
        }
    }, [modalSeen]);

    useEffect(() => {
        sessionStorage.setItem(keyModalSeen, modalSeen);
    }, [modalSeen, keyModalSeen]);

    const closeModal = () => {
        setIsShown(false);
        setModalSeen(true);
    };

    const onPrimaryAction = () => {
        const link = `/${ORGANIZATIONS}/${FINANCE_SETTINGS}/${BANK_ACCOUNTS}?country=${country}&currency=${currency}&eventId=${eventId}&openModal=true`;

        closeModal();
        navigateTo(link);
    };

    return (
        <Modal
            isShown={isShown}
            isPortal={true}
            title={gettext('Make sure you get paid')}
            noMinHeight={true}
            isClosable={false}
            disableCloseOnClickOutside={true}
            primaryText={gettext('Add bank account')}
            primaryType="fill"
            onPrimaryAction={onPrimaryAction}
            illustration={
                <Illustration
                    type={<HandWithMoneyGreen />}
                    height="350px"
                    width="auto"
                />
            }
            __containerClassName="payout-request-modal"
        >
            <ModalContent
                amount={localizedPayoutAmount}
                endDate={formattedEndDate}
                endTime={formattedEndTime}
                logo={logo}
                name={name}
            />
        </Modal>
    );
};
